import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ArrowClockwise, CurrencyBtc } from "@phosphor-icons/react";

function cellColor(pct) {
  // Higher minimum alpha (0.35) so tiles remain visible on white/light backgrounds;
  // saturation still climbs with |pct| so big movers stand out.
  if (pct === null || pct === undefined || isNaN(pct)) return "hsl(var(--muted))";
  const intensity = Math.min(1, Math.abs(pct) / 10);
  const alpha = 0.35 + intensity * 0.55;
  return pct >= 0
    ? `rgba(16,185,129,${alpha})`
    : `rgba(239,68,68,${alpha})`;
}

function fmtPrice(p) {
  if (p === null || p === undefined) return "—";
  if (p >= 1000) return `$${p.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (p >= 1) return `$${p.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  return `$${p.toLocaleString("en-US", { maximumFractionDigits: 6 })}`;
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  const s = v >= 0 ? "+" : "";
  return `${s}${v.toFixed(2)}%`;
}

export default function CryptoHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/market/crypto");
      setData(data);
      setRefreshedAt(new Date());
    } catch (e) {
      setError("Couldn't load market data. Retry shortly.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(int);
  }, []);

  const coins = data?.coins || [];
  const tg = data?.top_gainer;
  const tl = data?.top_loser;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4 sm:p-6" data-testid="crypto-heatmap-card">
        <div className="flex items-start justify-between mb-4 gap-2">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1">
              <CurrencyBtc size={12} /> Live Market
            </p>
            <h3 className="font-display text-base sm:text-lg font-semibold mt-1">Crypto Heatmap · 24h</h3>
            <p className="text-[10px] text-muted-foreground mt-0.5 hidden sm:block">
              Top 30 by market cap · CoinGecko · refreshes every 5 min
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5 sm:hidden">
              Top 30 · refreshes every 5 min
            </p>
          </div>
          <button onClick={load} disabled={loading} data-testid="crypto-refresh-btn"
            className="p-2 rounded-full border border-border hover:border-primary/50 disabled:opacity-40">
            <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {error && <p className="text-sm text-red-400 py-6 text-center" data-testid="crypto-error">{error}</p>}
        {!error && loading && coins.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading market data…</p>
        )}
        {!error && !loading && coins.length === 0 && (
          <p className="text-sm text-muted-foreground py-6 text-center">No market data available.</p>
        )}

        {coins.length > 0 && (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1.5 sm:gap-2" data-testid="crypto-grid">
              {coins.map(c => {
                const bg = cellColor(c.change_24h_pct);
                // Dark text in light theme (readable on pale-green/red tile);
                // light text in dark theme (readable on saturated-dark-green/red tile).
                const tone = c.change_24h_pct >= 0
                  ? "text-emerald-950 dark:text-emerald-50"
                  : "text-red-950 dark:text-red-50";
                const priceTone = c.change_24h_pct >= 0
                  ? "text-emerald-900/80 dark:text-white/70"
                  : "text-red-900/80 dark:text-white/70";
                return (
                  <div key={c.id} data-testid={`crypto-cell-${c.symbol}`}
                    className="rounded-lg border border-border/60 p-2 sm:p-3 transition-transform hover:-translate-y-0.5 min-w-0"
                    style={{ background: bg }}
                    title={`${c.name} · ${fmtPrice(c.price)} · ${fmtPct(c.change_24h_pct)}`}>
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                      {c.image && <img src={c.image} alt="" className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full shrink-0" />}
                      <p className={`text-[11px] sm:text-xs font-mono font-semibold ${tone} truncate`}>{c.symbol}</p>
                    </div>
                    <p className={`font-mono-num text-xs sm:text-sm font-bold mt-1 ${tone} truncate`}>{fmtPct(c.change_24h_pct)}</p>
                    <p className={`text-[10px] truncate ${priceTone}`}>{fmtPrice(c.price)}</p>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-3 mt-4 text-[10px] text-muted-foreground">
              <span>−10%</span>
              <div className="flex gap-1">
                {[0.9, 0.6, 0.3].map(o => (
                  <div key={"r"+o} className="w-3 h-3 rounded" style={{ background: `rgba(239,68,68,${o})` }} />
                ))}
                {[0.3, 0.6, 0.9].map(o => (
                  <div key={"g"+o} className="w-3 h-3 rounded" style={{ background: `rgba(16,185,129,${o})` }} />
                ))}
              </div>
              <span>+10%</span>
              <span className="ml-auto">
                {refreshedAt ? `Updated ${refreshedAt.toLocaleTimeString()}` : ""}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Top movers */}
      {(tg || tl) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tg && (
            <div className="rounded-xl border border-border bg-card p-4 sm:p-6 relative overflow-hidden"
              data-testid="crypto-top-gainer">
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-emerald-500/10 blur-2xl" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-600 dark:text-emerald-400">Top Gainer · 24h</p>
              <div className="flex items-center gap-3 mt-3 min-w-0">
                {tg.image && <img src={tg.image} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full shrink-0" />}
                <div className="min-w-0">
                  <p className="font-display text-base sm:text-xl font-semibold truncate">{tg.name}</p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-mono truncate">{tg.symbol}</p>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <p className="font-mono-num text-xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400" data-testid="top-gainer-pct">
                    {fmtPct(tg.change_24h_pct)}
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-mono">{fmtPrice(tg.price)}</p>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-3 truncate">
                Rank #{tg.market_cap_rank} · MCap ${(tg.market_cap || 0).toLocaleString("en-US")}
              </p>
            </div>
          )}

          {tl && (
            <div className="rounded-xl border border-border bg-card p-4 sm:p-6 relative overflow-hidden"
              data-testid="crypto-top-loser">
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-red-500/10 blur-2xl" />
              <p className="text-[10px] tracking-[0.2em] uppercase text-red-600 dark:text-red-400">Top Loser · 24h</p>
              <div className="flex items-center gap-3 mt-3 min-w-0">
                {tl.image && <img src={tl.image} alt="" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full shrink-0" />}
                <div className="min-w-0">
                  <p className="font-display text-base sm:text-xl font-semibold truncate">{tl.name}</p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-mono truncate">{tl.symbol}</p>
                </div>
                <div className="ml-auto text-right shrink-0">
                  <p className="font-mono-num text-xl sm:text-3xl font-bold text-red-400" data-testid="top-loser-pct">
                    {fmtPct(tl.change_24h_pct)}
                  </p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground font-mono">{fmtPrice(tl.price)}</p>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-3 truncate">
                Rank #{tl.market_cap_rank} · MCap ${(tl.market_cap || 0).toLocaleString("en-US")}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
